/** @format */

import {
	ATC_BUTTON_TEXTS,
	BUTTON_ACTIONS,
	BUTTON_LABELS,
	BUTTON_TEXTS,
	CHANGE_STORE_BUTTON_TEXTS,
	LABEL_TEXTS,
	LOW_STOCK_MESSAGE,
	STORE_CLOSED_MESSAGE
} from './fulfillment-buttons.constants';
import { BOPIS_AVAILABLE, OPEN_STORE_STATUS, StoreStatus } from '@shared/data-access/enums';
import {
	DisabledAttrOptions,
	selectDisabledAttributes,
	selectSelectedAttributeOptions
} from '@pdp/attributes/data-access';
import { FulfillmentState, SddInventory } from '@pdp/data-access/interfaces';
import {
	HeaderStoreSelection,
	ISkuLocation,
	ProductSku,
	ProductStyle,
	SkuDescriptiveAttribute
} from '@shared/data-access/interfaces';
import {
	createFeatureSelector,
	createSelector,
	createSelectorFactory,
	defaultMemoize
} from '@ngrx/store';
import {
	selectAllProductStyle,
	selectIsComingSoon,
	selectIsPreSale,
	selectNearbyAvailableStoreCount,
	selectResolvedSku,
	selectResolvedSkuBopisInventory,
	selectResolvedSkuEcommInventory,
	selectResolvedSkuSDDInventory,
	selectStoreNameTitleCase
} from '@pdp/data-access/state';

import { FulfillmentButtonInterface } from '../../interfaces/fulfillment-button.interface';
import { selectAtcUserInputQty } from '../add-to-cart.selectors';
import { selectPickupTimeText, selectUserStoreInfo, selectUserZipCode } from '@state/data-access';

/**
 * @description Same Day Delivery Fulfillment Button
 * Handles each different scenario for fulfillment functionality
 */

const getFullFillmentButtonTextInStockSDD = (
	descriptiveAttributes: SkuDescriptiveAttribute[],
	nonDynamicText: string,
	pickupTimeText: string
): string => {
	const isBike = descriptiveAttributes.some(
		(attr) => attr.name === 'Product Type' && attr.value === 'Bikes'
	);

	if (isBike) {
		return nonDynamicText;
	}

	return pickupTimeText;
};

export const selectFulfillmentState = createFeatureSelector<FulfillmentState>('fulfillment');

export const selectShipMode = createSelector(selectFulfillmentState, (state) => {
	return state?.fulfillmentButtonsReducer.shipMode;
});

export const selectNearbyStoresButtonEnabledFlag = createSelector(
	selectFulfillmentState,
	(state) => state?.fulfillmentButtonsReducer.nearbyStoresButtonEnabled
);

export const selectSameDayDeliveryButtonText = createSelector(
	selectResolvedSku,
	selectUserZipCode,
	selectResolvedSkuSDDInventory,
	selectAtcUserInputQty,
	(
		resolvedSku: ProductSku,
		userZipCode: string,
		sddInventory: SddInventory,
		quantityInput: number
	): FulfillmentButtonInterface => {
		if (!userZipCode || userZipCode === 'null') {
			const fulfillmentButtonContent: FulfillmentButtonInterface = {
				showZipCodeButton: true,
				fulfillmentButtonDisabledClass: false,
				addToCartButtonDisabled: false
			};
			if (resolvedSku === null) {
				fulfillmentButtonContent.addToCartButtonText = ATC_BUTTON_TEXTS.atc;
				fulfillmentButtonContent.buttonAction = BUTTON_ACTIONS.selectOptions;
				fulfillmentButtonContent.fulfillmentButtonText = 'Select product options';
				fulfillmentButtonContent.showZipCodeButton = false;
			} else {
				fulfillmentButtonContent.addToCartButtonText = ATC_BUTTON_TEXTS.noDeliveryLocation;
				fulfillmentButtonContent.fulfillmentButtonText = '';
				fulfillmentButtonContent.fulfillmentLabelTextLine1 = 'Same Day Delivery to';
			}
			return fulfillmentButtonContent;
		}
		if (resolvedSku === null) {
			return {
				fulfillmentButtonText: BUTTON_TEXTS.noSkuResolved,
				fulfillmentButtonDisabledClass: false,
				fulfillmentLabelTextLine1: LABEL_TEXTS.sdd,
				addToCartButtonText: ATC_BUTTON_TEXTS.atc,
				buttonAction: BUTTON_ACTIONS.selectOptions,
				addToCartButtonDisabled: false
			};
		}
		// In stock product available and same day delivery returns data and has inventory
		if (sddInventory && sddInventory.sameDayQuantity >= quantityInput) {
			return {
				fulfillmentButtonDisabledClass: false,
				fulfillmentLabelTextLine1: LABEL_TEXTS.sdd,
				sddAvailable: true,
				addToCartButtonText: ATC_BUTTON_TEXTS.atc,
				addToCartButtonDisabled: false,
				...(sddInventory.sameDayQuantity < 10 && {
					lowStockMessage: LOW_STOCK_MESSAGE(sddInventory.sameDayQuantity)
				})
			};
			// Product is not available for SDD
		}
		return {
			fulfillmentButtonText: BUTTON_TEXTS.notAvailableTo,
			fulfillmentButtonDisabledClass: true,
			showZipCodeButton: true,
			fulfillmentLabelTextLine1: LABEL_TEXTS.notAvailableToShip,
			addToCartButtonText: ATC_BUTTON_TEXTS.atc,
			addToCartButtonDisabled: true,
			notAvailable: true,
			isLowerQuantityAvailable: sddInventory?.sameDayQuantity > 0
		};
	}
);

export const selectShipToMeButtonText = createSelector(
	selectResolvedSku,
	selectResolvedSkuEcommInventory,
	selectIsPreSale,
	selectAtcUserInputQty,
	(
		resolvedSku: ProductSku,
		inventory: ISkuLocation,
		isPreSale: boolean,
		quantityInput: number
	): FulfillmentButtonInterface => {
		const amountAvailableToShip = parseInt(inventory?.atsqty, 10);
		// Not sku resolved
		if (resolvedSku === null) {
			return {
				fulfillmentButtonText: BUTTON_TEXTS.noSkuResolved,
				fulfillmentLabelTextLine1: LABEL_TEXTS.shipTo,
				fulfillmentButtonDisabledClass: false,
				addToCartButtonText: ATC_BUTTON_TEXTS.atc,
				buttonAction: BUTTON_ACTIONS.selectOptions,
				addToCartButtonDisabled: false
			};
		}
		if (inventory && amountAvailableToShip >= quantityInput) {
			return {
				fulfillmentButtonText: BUTTON_TEXTS.available,
				fulfillmentLabelTextLine1: LABEL_TEXTS.shipTo,
				fulfillmentButtonDisabledClass: false,
				showEDD: true,
				addToCartButtonText: isPreSale ? ATC_BUTTON_TEXTS.preOrder : ATC_BUTTON_TEXTS.atc,
				addToCartButtonDisabled: false,
				...(amountAvailableToShip < 10 && {
					lowStockMessage: LOW_STOCK_MESSAGE(amountAvailableToShip)
				})
			};
		}
		// Product is not available for Ship To Me
		return {
			fulfillmentButtonText: BUTTON_TEXTS.notAvailable,
			fulfillmentButtonDisabledClass: true,
			fulfillmentLabelTextLine1: LABEL_TEXTS.notAvailableToShip,
			addToCartButtonText: resolvedSku?.onOrder
				? ATC_BUTTON_TEXTS.notifyMe
				: ATC_BUTTON_TEXTS.atc,
			addToCartButtonDisabled: !resolvedSku?.onOrder,
			showOutOfStockRecs: true,
			isLowerQuantityAvailable: amountAvailableToShip > 0,
			notAvailable: true
		};
	}
);

export const selectInStorePickupButtonText = createSelectorFactory(defaultMemoize)(
	selectResolvedSku,
	selectStoreNameTitleCase,
	selectResolvedSkuBopisInventory,
	selectNearbyAvailableStoreCount,
	selectUserStoreInfo,
	selectAtcUserInputQty,
	selectPickupTimeText,
	selectNearbyStoresButtonEnabledFlag,
	(
		resolvedSku: ProductSku,
		storeName: string,
		inventory: ISkuLocation | null,
		nearbyStores: number,
		userStoreInfo: HeaderStoreSelection,
		quantityInput: number,
		pickupTimeText: string,
		nearbyStoresButtonEnabledFlag: boolean
	): FulfillmentButtonInterface => {
		const amountAvailableForBopis = parseInt(inventory?.atsqty, 10);
		const amountAvailableToShipToStore = parseInt(inventory?.boplqty, 10);
		const amountAvailableToBuyInStore = parseInt(inventory?.isaqty, 10);

		const storeIsOpenForBopis = BOPIS_AVAILABLE.includes(userStoreInfo?.status as StoreStatus);
		const storeIsOpenForFootTraffic = OPEN_STORE_STATUS.includes(
			userStoreInfo?.status as StoreStatus
		);

		if (resolvedSku === null) {
			return {
				fulfillmentButtonLabel: BUTTON_LABELS.storePickup,
				fulfillmentButtonText: BUTTON_TEXTS.noSkuResolved,
				fulfillmentButtonDisabledClass: false,
				addToCartButtonText: ATC_BUTTON_TEXTS.atc,
				buttonAction: BUTTON_ACTIONS.selectOptions,
				addToCartButtonDisabled: false
			};
		}
		if (!storeName) {
			return {
				fulfillmentButtonLabel: BUTTON_LABELS.storePickup,
				changeStoreButtonText: CHANGE_STORE_BUTTON_TEXTS.noStoreSelected,
				fulfillmentButtonDisabledClass: false,
				addToCartButtonText: ATC_BUTTON_TEXTS.noStoreSelected,
				addToCartButtonDisabled: false
			};
		}
		if (amountAvailableForBopis >= quantityInput && storeIsOpenForBopis) {
			return {
				fulfillmentButtonLabel: BUTTON_LABELS.storePickup,
				nonDynamicFullfillmentButtonText: BUTTON_TEXTS.inStockAtStore(storeName),
				fulfillmentButtonText: getFullFillmentButtonTextInStockSDD(
					resolvedSku.descriptiveAttributes,
					BUTTON_TEXTS.inStockAtStore(storeName),
					pickupTimeText
				),
				fulfillmentButtonDisabledClass: false,
				fulfillmentLabelTextLine1: LABEL_TEXTS.pickupAtStore,
				addToCartButtonText: ATC_BUTTON_TEXTS.atc,
				addToCartButtonDisabled: false,
				...(amountAvailableForBopis < 10 && {
					lowStockMessage: LOW_STOCK_MESSAGE(amountAvailableForBopis)
				})
			};
		}
		if (amountAvailableToShipToStore >= quantityInput && storeIsOpenForBopis) {
			return {
				fulfillmentButtonLabel: BUTTON_LABELS.shipToStore,
				fulfillmentButtonText: BUTTON_TEXTS.shipToStore,
				fulfillmentLabelTextLine1: LABEL_TEXTS.pickupAtStore,
				fulfillmentButtonDisabledClass: false,
				addToCartButtonText: ATC_BUTTON_TEXTS.atc,
				addToCartButtonDisabled: false,
				...(amountAvailableToShipToStore < 10 && {
					lowStockMessage: LOW_STOCK_MESSAGE(amountAvailableToShipToStore)
				}),
				isBopl: true
			};
		}
		if (amountAvailableToBuyInStore >= quantityInput && storeIsOpenForFootTraffic) {
			return {
				fulfillmentButtonLabel: BUTTON_LABELS.storePickup,
				fulfillmentButtonText: BUTTON_TEXTS.purchaseInStore,
				fulfillmentButtonDisabledClass: true,
				fulfillmentLabelTextLine1: LABEL_TEXTS.inStockAtStore,
				addToCartButtonText: ATC_BUTTON_TEXTS.atc,
				addToCartButtonDisabled: true,
				...(amountAvailableToBuyInStore < 10 && {
					lowStockMessage: LOW_STOCK_MESSAGE(amountAvailableToBuyInStore)
				}),
				availableInStoreOnly: true
			};
		}
		const storeClosed = !storeIsOpenForBopis || !storeIsOpenForFootTraffic;

		const notAvailableATCText = resolvedSku?.onOrder
			? ATC_BUTTON_TEXTS.notifyMe
			: ATC_BUTTON_TEXTS.atc;

		// Build up the "not available" content
		const notAvailableContent: FulfillmentButtonInterface = {
			fulfillmentButtonLabel: BUTTON_LABELS.storePickup,
			fulfillmentLabelTextLine1: LABEL_TEXTS.notAvailableAt,
			addToCartButtonDisabled: !resolvedSku?.onOrder,
			showOutOfStockRecs: true,
			isLowerQuantityAvailable:
				amountAvailableForBopis > 0 ||
				amountAvailableToShipToStore > 0 ||
				amountAvailableToBuyInStore > 0,
			notAvailable: true
		};

		if (storeClosed) {
			notAvailableContent.storeClosedMessage = STORE_CLOSED_MESSAGE.temporarilyClosed;

			/**
			 * Get the first non-zero amount of stock. Mirrors the order we check, so the amount
			 * displayed is the same as if the store wasn't closed.
			 */
			const storeClosedLowStock =
				amountAvailableForBopis ||
				amountAvailableToShipToStore ||
				amountAvailableToBuyInStore;
			if (storeClosedLowStock > 0) {
				notAvailableContent.fulfillmentLabelTextLine1 = LABEL_TEXTS.pickupAtStore;
				notAvailableContent.isLowerQuantityAvailable = storeClosedLowStock < quantityInput;

				if (storeClosedLowStock < 10) {
					notAvailableContent.lowStockMessage = LOW_STOCK_MESSAGE(storeClosedLowStock);
				}
			}
		}

		if (nearbyStores > 0) {
			notAvailableContent.fulfillmentButtonDisabledClass = false;
			notAvailableContent.changeStoreButtonText = CHANGE_STORE_BUTTON_TEXTS.nearbyStores;
			notAvailableContent.changeStoreButtonDataEm = 'PDP_seeAvailableStores_card';
			notAvailableContent.addToCartButtonText = nearbyStoresButtonEnabledFlag
				? ATC_BUTTON_TEXTS.nearbyStores
				: notAvailableATCText;
		} else {
			notAvailableContent.fulfillmentButtonText = storeClosed
				? BUTTON_TEXTS.storeClosed
				: BUTTON_TEXTS.notAvailable;
			notAvailableContent.fulfillmentButtonDisabledClass = true;
			notAvailableContent.addToCartButtonText = notAvailableATCText;
		}

		return notAvailableContent;
	}
);

export const selectOptionNotAvailable = createSelector(
	selectDisabledAttributes,
	selectSelectedAttributeOptions,
	(
		disabledAttributes: DisabledAttrOptions,
		selectedAttributeOptions: { name: string; value: string }[]
	) => {
		let optionUnavailable = false;

		if (!selectSelectedAttributeOptions) {
			return false;
		}

		Object.keys(disabledAttributes).forEach((attrKey) => {
			const selectedOption = selectedAttributeOptions.find(
				(val) => val.name.toLowerCase() === attrKey.toLowerCase()
			);

			if (selectedOption && !disabledAttributes[attrKey].includes(selectedOption.value)) {
				optionUnavailable = true;
			}
		});

		return optionUnavailable;
	}
);

export const selectShouldShowOutOfStockRecs = createSelector(
	selectShipToMeButtonText,
	selectInStorePickupButtonText,
	(shipToMeText: FulfillmentButtonInterface, inStorePickupText: FulfillmentButtonInterface) =>
		shipToMeText.showOutOfStockRecs && inStorePickupText.showOutOfStockRecs
);

export const selectIsInStoreOnlyProduct = createSelector(
	selectAllProductStyle,
	selectIsComingSoon,
	// return whether or not the ecode product is in store only
	(productStyle: ProductStyle | Record<string, never>, isComingSoon: boolean) => {
		return productStyle && productStyle.buyable === 0 && !isComingSoon;
	}
);
